import { env } from '@/env'
import { z } from 'zod'
import {
  TENANT_HEADER,
  TOKEN_STORAGE_KEY,
  readStoredTenantId,
} from './tenant'

export interface ApiError {
  code: string
  message: string
  retryable: boolean
}

export interface ApiResponse<T> {
  data: T
  meta?: {
    nextCursor: string | null
    hasMore: boolean
    totalCount: number
  }
}

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly retryable: boolean,
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }
}

/** Raised when a response body does not match its declared contract. */
export class ApiContractError extends Error {
  constructor(
    public readonly path: string,
    public readonly issues: z.ZodIssue[],
  ) {
    super(
      `Response contract violation for ${path}: ` +
        issues.map((i) => `${i.path.join('.') || '<root>'} ${i.message}`).join('; '),
    )
    this.name = 'ApiContractError'
  }
}

/**
 * Query parameters may be any plain object. Typed filter interfaces (e.g.
 * `AnalyticsFilters`) do not carry an index signature, so the parameter is
 * intentionally permissive and iterated defensively at runtime.
 */
export type QueryParams = object

export interface RequestOptions<T = unknown> extends Omit<RequestInit, 'body'> {
  tenantId?: string
  idempotencyKey?: string
  params?: QueryParams
  body?: unknown
  /**
   * Optional runtime contract. When provided, the response body is validated
   * with zod before it reaches application code (fail loud on drift).
   */
  schema?: z.ZodType<T>
}

/**
 * Single place where outbound headers are constructed.
 * Tenant identity is never invented here: it comes from the caller or storage.
 */
export function buildRequestHeaders(
  init: RequestInit = {},
  options: { tenantId?: string | null; idempotencyKey?: string } = {},
): Headers {
  const headers = new Headers(init.headers)
  headers.set('Content-Type', 'application/json')

  const token = localStorage.getItem(TOKEN_STORAGE_KEY)
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const tenantId = options.tenantId ?? readStoredTenantId()
  if (tenantId) headers.set(TENANT_HEADER, tenantId)

  if (options.idempotencyKey) {
    headers.set('Idempotency-Key', options.idempotencyKey)
  }
  return headers
}

async function request<T>(path: string, options: RequestOptions<T> = {}): Promise<T> {
  const { tenantId, idempotencyKey, params, schema, body, ...init } = options
  const headers = buildRequestHeaders(init, { tenantId, idempotencyKey })

  let resolvedPath = path
  if (params) {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
      if (v == null) continue
      if (Array.isArray(v)) {
        v.forEach((item) => qs.append(k, String(item)))
      } else {
        qs.set(k, String(v))
      }
    }
    const str = qs.toString()
    if (str) resolvedPath = `${path}?${str}`
  }

  const url = `${env.bffUrl}${resolvedPath}`
  const res = await fetch(url, {
    ...init,
    method: init.method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!res.ok) {
    let errBody: { code?: string; message?: string; retryable?: boolean } = {}
    try {
      errBody = await res.json()
    } catch {
      // non-JSON error body
    }
    throw new ApiRequestError(
      res.status,
      errBody.code ?? 'UNKNOWN_ERROR',
      errBody.message ?? `HTTP ${res.status}`,
      errBody.retryable ?? res.status >= 500,
    )
  }

  if (res.status === 204) return undefined as T

  const json: unknown = await res.json()
  if (!schema) return json as T

  const parsed = schema.safeParse(json)
  if (!parsed.success) throw new ApiContractError(resolvedPath, parsed.error.issues)
  return parsed.data
}

export const api = {
  get: <T>(path: string, params?: QueryParams, opts?: RequestOptions<T>) =>
    request<T>(path, { ...opts, method: 'GET', params }),
  post: <T>(path: string, body: unknown, opts?: RequestOptions<T>) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body: unknown, opts?: RequestOptions<T>) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body: unknown, opts?: RequestOptions<T>) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: RequestOptions<T>) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
}

